/**
 * #3 會員權益人管理
 *
 * 主會員可設 1-5 位權益人（家人、配偶等）。
 * 權益人可被指定為送禮收件人（#13 Week 3）。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addBeneficiary, getMe, removeBeneficiary, type Beneficiary } from "../api/members";
import { useMemberStore } from "../store/memberStore";
import { useUIStore } from "../store/uiStore";

const RELATIONS = ["配偶", "父母", "子女", "兄弟姊妹", "朋友", "其他"];

export default function Beneficiaries() {
  const navigate = useNavigate();
  const member = useMemberStore((s) => s.member);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [relation, setRelation] = useState(RELATIONS[0]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const m = await getMe();
      useMemberStore.getState().setMember(m);
    })();
  }, []);

  async function refresh() {
    const m = await getMe();
    useMemberStore.getState().setMember(m);
  }

  async function add() {
    if (!name.trim() || !relation) {
      useUIStore.getState().showToast("姓名 / 關係必填", "error");
      return;
    }
    setBusy(true);
    try {
      const r = await addBeneficiary({ name, phone, relation });
      if (r.status === "ok") {
        useUIStore.getState().showToast("已新增", "success");
        setName("");
        setPhone("");
        setRelation(RELATIONS[0]);
        setOpen(false);
        await refresh();
      } else if (r.status === "limit_reached") {
        useUIStore.getState().showToast(`已達上限（${r.limit} 位）`, "error");
      } else if (r.status === "duplicate_phone") {
        useUIStore.getState().showToast("此手機已新增過", "error");
      } else {
        useUIStore.getState().showToast(`新增失敗：${r.status}`, "error");
      }
    } finally {
      setBusy(false);
    }
  }

  async function remove(b: Beneficiary) {
    if (!confirm(`移除權益人「${b.name}」？`)) return;
    await removeBeneficiary(b.phone);
    useUIStore.getState().showToast("已移除", "success");
    await refresh();
  }

  const list: Beneficiary[] = member?.beneficiaries || [];

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white text-xl">‹</button>
        <h1 className="text-xl font-bold">權益人</h1>
      </header>

      <div className="px-5 mt-5">
        <div className="text-sm text-muted mb-3">
          可設定 1-5 位家人或友人作為權益人，他們可被指定為送禮收件人。
        </div>

        <div className="space-y-3">
          {list.length === 0 && (
            <div className="text-center text-muted text-sm py-8">尚未新增權益人</div>
          )}
          {list.map((b) => (
            <div key={b.phone || b.name} className="card flex items-start justify-between">
              <div>
                <div className="font-medium text-ink">{b.name}</div>
                <div className="text-sm text-muted">
                  {b.relation}
                  {b.phone ? ` · ${b.phone}` : ""}
                </div>
              </div>
              <button onClick={() => remove(b)} className="text-sm text-red-600">移除</button>
            </div>
          ))}
        </div>

        {!open ? (
          <button onClick={() => setOpen(true)} className="btn-primary w-full mt-6">
            ＋ 新增權益人
          </button>
        ) : (
          <div className="card mt-6 space-y-3">
            <div>
              <label className="label">姓名 *</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="label">手機（可選）</label>
              <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <label className="label">關係 *</label>
              <select className="input" value={relation} onChange={(e) => setRelation(e.target.value)}>
                {RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setOpen(false)} className="btn-secondary flex-1">取消</button>
              <button onClick={add} disabled={busy} className="btn-primary flex-1">
                {busy ? "新增中…" : "新增"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
